import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Label,
  Row,
  Button,
  Table,
} from "reactstrap";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Swal from "sweetalert2";
import { ClipLoader } from "react-spinners";
import * as XLSX from "xlsx";
import { useTranslation } from "react-i18next";
import { FileSpreadsheet, Printer, Search, RefreshCw, ChevronLeft, ChevronRight } from "lucide-react";

import { getBusinesses } from "../../components/Api/GmsBusiness";
import { getBranches } from "../../components/Api/GmsBranch";
import {
  getAccountMasters,
  getAccountHeads,
  getAccountSubHeads,
} from "../../components/Api/GmsFinance";
import { getCustomers } from "../../components/Api/GmsCustomer";
import { getSuppliers } from "../../components/Api/GmsSupplier";
import { getBankAccounts } from "../../components/Api/GmsBankAccount";
import { getEmployees } from "../../components/Api/GmsEmployee";
import { getOtherPayees } from "../../components/Api/GmsOtherPayee";
import { getGeneralLedgerReport } from "../../components/Api/GmsLedgerPosting";
import { hasPagePermissionWithAdmin } from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";
import { formatDateForDisplay, formatDateForAPI } from "../../helpers/dateHelper";

// Custom styles for Select components
const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '38px',
    borderColor: state.isFocused ? '#556ee6' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
  }),
};

// Get start and end of current month
const getStartOfCurrentMonth = () => {
  const date = new Date();
  return new Date(date.getFullYear(), date.getMonth(), 1);
};

const getEndOfCurrentMonth = () => {
  const date = new Date();
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
};

// Get user session
const getUserSession = () => {
  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  const normalizeId = (value) => {
    if (value === null || value === undefined || value === '' || value === 'null' || value === 'undefined') {
      return null;
    }
    const numValue = typeof value === 'string' ? parseInt(value, 10) : value;
    return isNaN(numValue) || numValue <= 0 ? null : numValue;
  };

  let businessId, branchId;
  if (authUser && Object.keys(authUser).length > 0) {
    businessId = normalizeId(authUser.companyId);
    branchId = normalizeId(authUser.branchId);
  } else {
    businessId = normalizeId(user.user_Company_Id || localStorage.getItem("userCompanyId"));
    branchId = normalizeId(user.user_Branch_Id || localStorage.getItem("userBranchId"));
  }

  return {
    userId: authUser.userId || 1,
    userName: authUser.userName || "System",
    businessId: businessId,
    branchId: branchId,
  };
};

export default function GeneralLedgerReportPage() {
  const { t } = useTranslation();
  const session = useMemo(() => getUserSession(), []);
  const printRef = useRef(null);

  // Permission states
  const permissions = useMemo(() => JSON.parse(localStorage.getItem("permissions") || "[]"), []);
  const hasPermission = useMemo(
    () => hasPagePermissionWithAdmin(permissions, "/GmsGeneralLedgerReport"),
    [permissions]
  );

  // Core Data States
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);

  // Pagination States
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Dropdown Options States
  const [businesses, setBusinesses] = useState([]);
  const [branches, setBranches] = useState([]);
  const [masters, setMasters] = useState([]);
  const [heads, setHeads] = useState([]);
  const [subHeads, setSubHeads] = useState([]);
  const [transactors, setTransactors] = useState([]);

  // Filter States
  const [fromDate, setFromDate] = useState(getStartOfCurrentMonth());
  const [toDate, setToDate] = useState(getEndOfCurrentMonth());
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [selectedMaster, setSelectedMaster] = useState(null);
  const [selectedHead, setSelectedHead] = useState(null);
  const [selectedSubHead, setSelectedSubHead] = useState(null);
  const [transactorType, setTransactorType] = useState(null);
  const [selectedTransactor, setSelectedTransactor] = useState(null);
  const [amountFilter, setAmountFilter] = useState(null);
  const [amountVal, setAmountVal] = useState("");

  const [loadingMaster, setLoadingMaster] = useState(false);
  const [loadingTransactors, setLoadingTransactors] = useState(false);

  // Constants
  const transactorTypeOptions = [
    { value: "Customer", label: t("Customer") },
    { value: "Supplier", label: t("Supplier") },
    { value: "Bank", label: t("Bank") },
    { value: "Employee", label: t("Employee") },
    { value: "Other", label: t("Other Payee") },
  ];

  const amountFilterOptions = [
    { value: "=", label: "=" },
    { value: ">", label: ">" },
    { value: "<", label: "<" },
  ];

  // Helper for transaction badges
  const getTransactionTypeBadgeClass = (type) => {
    const t = (type || "").toLowerCase();
    if (t.includes("sales") || t.includes("invoice")) return "success";
    if (t.includes("purchase")) return "primary";
    if (t.includes("receipt") || t.includes("dep")) return "info";
    if (t.includes("payment") || t.includes("pay")) return "danger";
    if (t.includes("expense") || t.includes("cash")) return "warning";
    return "secondary";
  };

  // Calculations
  const { totalDebits, totalCredits, netBalance } = useMemo(() => {
    let debits = 0;
    let credits = 0;
    reportData.forEach((item) => {
      debits += item.debitAmt || 0;
      credits += item.creditAmt || 0;
    });
    return {
      totalDebits: debits,
      totalCredits: credits,
      netBalance: debits - credits,
    };
  }, [reportData]);

  // Reset page when report data or page size changes
  useEffect(() => {
    setCurrentPage(1);
  }, [reportData, pageSize]);

  // Pagination Calculations
  const totalCount = reportData.length;
  const totalPages = Math.ceil(totalCount / pageSize);

  const paginatedData = useMemo(() => {
    if (pageSize === -1) return reportData;
    const startIndex = (currentPage - 1) * pageSize;
    return reportData.slice(startIndex, startIndex + pageSize);
  }, [reportData, currentPage, pageSize]);

  // Load Initial Metadata
  useEffect(() => {
    if (!hasPermission) {
      window.location.href = "/Dashboard";
      return;
    }
    loadBusinesses();
    loadMasters();
  }, [hasPermission]);

  // Handle Business Selection Changes
  useEffect(() => {
    if (selectedBusiness) {
      loadBranches(selectedBusiness.value);
    } else {
      setBranches([]);
      setSelectedBranch(null);
    }
  }, [selectedBusiness]);

  // Handle Master Selection Changes
  useEffect(() => {
    if (selectedMaster) {
      loadHeads(selectedMaster.value);
    } else {
      setHeads([]);
      setSelectedHead(null);
      setSubHeads([]);
      setSelectedSubHead(null);
    }
  }, [selectedMaster]);

  // Handle Head Selection Changes
  useEffect(() => {
    if (selectedHead) {
      loadSubHeads(selectedHead.value);
    } else {
      setSubHeads([]);
      setSelectedSubHead(null);
    }
  }, [selectedHead]);

  // Handle Transactor Type Selection Changes
  useEffect(() => {
    if (transactorType) {
      loadTransactors(transactorType.value);
    } else {
      setTransactors([]);
      setSelectedTransactor(null);
    }
  }, [transactorType]);

  // Fetch report automatically on load once businesses are fetched
  useEffect(() => {
    if (hasPermission && businesses.length > 0) {
      handleSearch();
    }
  }, [businesses]);

  const loadBusinesses = async () => {
    try {
      setLoadingMaster(true);
      const data = await getBusinesses({ business_Active: true });
      const businessData = Array.isArray(data) ? data : (data.data || []);
      const options = businessData.map(b => ({
        value: b.business_Id,
        label: b.business_NameEn
      }));
      setBusinesses(options);

      // Default to session business
      if (session.businessId) {
        const defaultBus = options.find(o => o.value === session.businessId);
        if (defaultBus) {
          setSelectedBusiness(defaultBus);
        }
      }
    } catch (error) {
      console.error("Error loading businesses:", error);
    } finally {
      setLoadingMaster(false);
    }
  };

  const loadBranches = async (businessId) => {
    try {
      setLoadingMaster(true);
      const data = await getBranches({ Business_Id: businessId, branch_Active: true });
      const branchData = Array.isArray(data) ? data : (data.data || []);
      const options = branchData.map(br => ({
        value: br.branch_Id,
        label: br.branch_NameEn
      }));
      setBranches(options);

      // Default to session branch
      if (session.branchId) {
        const defaultBranch = options.find(o => o.value === session.branchId);
        if (defaultBranch) {
          setSelectedBranch(defaultBranch);
        }
      } else {
        setSelectedBranch(null);
      }
    } catch (error) {
      console.error("Error loading branches:", error);
    } finally {
      setLoadingMaster(false);
    }
  };

  const loadMasters = async () => {
    try {
      const data = await getAccountMasters();
      const list = Array.isArray(data) ? data : [];
      setMasters(list.map(m => ({ value: m.accountMasterID, label: m.accountMasterNameEn })));
    } catch (error) {
      console.error("Error loading account masters:", error);
    }
  };

  const loadHeads = async (masterId) => {
    try {
      const data = await getAccountHeads(masterId);
      const list = Array.isArray(data) ? data : [];
      setHeads(list.map(h => ({ value: h.accountHeadID, label: h.accountHeadNameEn })));
    } catch (error) {
      console.error("Error loading account heads:", error);
    }
  };

  const loadSubHeads = async (headId) => {
    try {
      const data = await getAccountSubHeads(headId);
      const list = Array.isArray(data) ? data : [];
      setSubHeads(list.map(s => ({ value: s.accountSubHeadID, label: s.accountSubHeadNameEn })));
    } catch (error) {
      console.error("Error loading account sub-heads:", error);
    }
  };

  const loadTransactors = async (type) => {
    try {
      setLoadingTransactors(true);
      setSelectedTransactor(null);
      setTransactors([]);

      let rawData = [];
      if (type === "Customer") {
        rawData = await getCustomers();
      } else if (type === "Supplier") {
        rawData = await getSuppliers({});
      } else if (type === "Bank") {
        rawData = await getBankAccounts({});
      } else if (type === "Employee") {
        rawData = await getEmployees();
      } else if (type === "Other") {
        rawData = await getOtherPayees();
      }

      let list = [];
      if (type === "Customer") {
        list = rawData.map(c => ({ value: c.customer_Id, label: c.customer_NameEn }));
      } else if (type === "Supplier") {
        list = rawData.map(s => ({ value: s.supplier_Id, label: s.supplier_NameEn }));
      } else if (type === "Bank") {
        const bankAccountsList = rawData.bankAccounts || rawData || [];
        list = bankAccountsList.map(b => ({
          value: b.bankAccountId || b.bankAccount_Id,
          label: `${b.accountHolderName || ''} - ${b.accountNumber || ''}`
        }));
      } else if (type === "Employee") {
        list = rawData.map(e => ({ value: e.employee_Id, label: e.employee_NameEn }));
      } else if (type === "Other") {
        list = rawData.map(o => ({ value: o.otherPayee_Id, label: o.otherPayee_Name }));
      }

      setTransactors(list);
    } catch (error) {
      console.error("Error loading transactors:", error);
    } finally {
      setLoadingTransactors(false);
    }
  };

  const handleSearch = async () => {
    try {
      setLoading(true);
      const filterPayload = {
        FromDate: fromDate ? formatDateForAPI(fromDate) : null,
        ToDate: toDate ? formatDateForAPI(toDate) : null,
        TransactorType: transactorType ? transactorType.value : null,
        TransactorId: selectedTransactor ? selectedTransactor.value : null,
        AccountMasterID: selectedMaster ? selectedMaster.value : null,
        AccountHeadID: selectedHead ? selectedHead.value : null,
        AccountSubHeadID: selectedSubHead ? selectedSubHead.value : null,
        AmountFilter: amountFilter ? amountFilter.value : null,
        Amount: amountVal ? parseFloat(amountVal) : null,
        BusinessId: selectedBusiness ? selectedBusiness.value : session.businessId,
        BranchId: selectedBranch ? selectedBranch.value : session.branchId,
      };

      const result = await getGeneralLedgerReport(filterPayload);
      setReportData(Array.isArray(result) ? result : []);
    } catch (error) {
      console.error("Error fetching report:", error);
      Swal.fire(t("Error"), t("Failed to generate report"), "error");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFromDate(getStartOfCurrentMonth());
    setToDate(getEndOfCurrentMonth());
    setSelectedBusiness(businesses.find(o => o.value === session.businessId) || null);
    setSelectedBranch(null);
    setSelectedMaster(null);
    setSelectedHead(null);
    setSelectedSubHead(null);
    setTransactorType(null);
    setSelectedTransactor(null);
    setAmountFilter(null);
    setAmountVal("");
    setReportData([]);
  };

  const handleExportToExcel = () => {
    if (!reportData || reportData.length === 0) {
      Swal.fire(t("Warning"), t("No data available to export"), "warning");
      return;
    }

    try {
      const dataToExport = reportData.map((item, index) => ({
        [t("S.No")]: index + 1,
        [t("Date")]: formatDateForDisplay(item.transactionDate),
        [t("Business")]: item.businessName || "N/A",
        [t("Branch")]: item.branchName || "N/A",
        [t("Reference No")]: item.referenceNo || "N/A",
        [t("Transaction Type")]: item.transactionType,
        [t("Account Master")]: item.accountMasterName,
        [t("Account Head")]: item.accountHeadName,
        [t("Account Sub-Head")]: item.accountSubHeadName,
        [t("Transactor Type")]: item.transactorType || "N/A",
        [t("Transactor Name")]: item.transactorName || "N/A",
        [t("Debit (Dr)")]: item.debitAmt,
        [t("Credit (Cr)")]: item.creditAmt,
        [t("Notes")]: item.notes || ""
      }));

      const ws = XLSX.utils.json_to_sheet(dataToExport);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "General Ledger");

      // Column widths
      const maxLens = {};
      dataToExport.forEach(row => {
        Object.keys(row).forEach(key => {
          const val = row[key] ? row[key].toString() : '';
          maxLens[key] = Math.max(maxLens[key] || 10, val.length);
        });
      });
      ws['!cols'] = Object.keys(maxLens).map(key => ({ wch: maxLens[key] + 3 }));

      XLSX.writeFile(wb, `General_Ledger_Report_${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (error) {
      console.error("Excel export error:", error);
      Swal.fire(t("Error"), t("Failed to export report to Excel"), "error");
    }
  };

  const handlePrint = () => {
    if (!reportData || reportData.length === 0) {
      Swal.fire(t("Warning"), t("No data available to print"), "warning");
      return;
    }

    const printContents = printRef.current.innerHTML;
    const originalContents = document.body.innerHTML;

    // Create a simple printing template style
    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <html>
        <head>
          <title>${t("General Ledger Report")}</title>
          <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.3/dist/css/bootstrap.min.css">
          <style>
            body { font-family: 'Outfit', sans-serif; padding: 20px; color: #333; }
            .header-info { margin-bottom: 25px; border-bottom: 2px solid #556ee6; padding-bottom: 15px; }
            .summary-cards-print { display: flex; justify-content: space-between; margin-bottom: 25px; }
            .print-card { border: 1px solid #ddd; padding: 15px; border-radius: 5px; flex: 1; margin: 0 10px; text-align: center; }
            .print-card h6 { margin: 0 0 5px 0; color: #666; font-size: 13px; }
            .print-card h3 { margin: 0; font-size: 20px; font-weight: bold; color: #111; }
            table { width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 12px; }
            th { background-color: #f3f4f6 !important; color: #111 !important; border: 1px solid #ddd; padding: 8px; text-align: left; }
            td { border: 1px solid #ddd; padding: 8px; }
            .text-end { text-align: right; }
            .badge { padding: 4px 8px; border-radius: 4px; font-size: 10px; font-weight: bold; text-transform: uppercase; }
            @media print {
              .no-print { display: none; }
              body { padding: 0; }
            }
          </style>
        </head>
        <body onload="window.print();window.close();">
          <div class="header-info">
            <h2 style="color: #556ee6; margin-bottom: 5px;">${t("General Ledger Report")}</h2>
            <p style="margin: 0; font-size: 13px; color: #666;">
              ${t("Date Range")}: ${formatDateForDisplay(fromDate)} ${t("to")} ${formatDateForDisplay(toDate)}
            </p>
          </div>
          <div class="summary-cards-print">
            <div class="print-card">
              <h6>${t("Total Debits")}</h6>
              <h3>${totalDebits.toFixed(2)}</h3>
            </div>
            <div class="print-card">
              <h6>${t("Total Credits")}</h6>
              <h3>${totalCredits.toFixed(2)}</h3>
            </div>
            <div class="print-card">
              <h6>${t("Net Balance")}</h6>
              <h3>${netBalance.toFixed(2)}</h3>
            </div>
          </div>
          ${printContents}
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  if (!hasPermission) {
    return null;
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumb title={t("Reports")} breadcrumbItem={t("General Ledger Report")} />

          {/* Filter Card */}
          <Card className="shadow-sm border-0 mb-4">
            <CardBody>
              <h5 className="card-title mb-4" style={{ fontWeight: 600, color: "#495057" }}>
                <Search size={18} className="me-2 text-primary" style={{ verticalAlign: 'middle' }} />
                {t("Report Filters")}
              </h5>
              <Row className="g-3">
                {/* From Date */}
                <Col md={3}>
                  <Label className="form-label">{t("From Date")}</Label>
                  <div className="w-100">
                    <DatePicker
                      selected={fromDate}
                      onChange={(date) => setFromDate(date)}
                      className="form-control w-100"
                      placeholderText={t("Choose Start Date")}
                      dateFormat="dd/MM/yyyy"
                    />
                  </div>
                </Col>

                {/* To Date */}
                <Col md={3}>
                  <Label className="form-label">{t("To Date")}</Label>
                  <div className="w-100">
                    <DatePicker
                      selected={toDate}
                      onChange={(date) => setToDate(date)}
                      className="form-control w-100"
                      placeholderText={t("Choose End Date")}
                      dateFormat="dd/MM/yyyy"
                    />
                  </div>
                </Col>

                {/* Business Selector */}
                <Col md={3}>
                  <Label className="form-label">{t("Business")}</Label>
                  <Select
                    value={selectedBusiness}
                    onChange={(option) => setSelectedBusiness(option)}
                    options={businesses}
                    styles={selectStyles}
                    isClearable
                    placeholder={t("All Businesses")}
                    isLoading={loadingMaster}
                  />
                </Col>

                {/* Branch Selector */}
                <Col md={3}>
                  <Label className="form-label">{t("Branch")}</Label>
                  <Select
                    value={selectedBranch}
                    onChange={(option) => setSelectedBranch(option)}
                    options={branches}
                    styles={selectStyles}
                    isClearable
                    placeholder={t("All Branches")}
                    disabled={!selectedBusiness}
                    isLoading={loadingMaster}
                  />
                </Col>

                {/* Account Master */}
                <Col md={3}>
                  <Label className="form-label">{t("Account Master")}</Label>
                  <Select
                    value={selectedMaster}
                    onChange={(option) => setSelectedMaster(option)}
                    options={masters}
                    styles={selectStyles}
                    isClearable
                    placeholder={t("All Masters")}
                  />
                </Col>

                {/* Account Head */}
                <Col md={3}>
                  <Label className="form-label">{t("Account Head")}</Label>
                  <Select
                    value={selectedHead}
                    onChange={(option) => setSelectedHead(option)}
                    options={heads}
                    styles={selectStyles}
                    isClearable
                    placeholder={t("All Heads")}
                    disabled={!selectedMaster}
                  />
                </Col>

                {/* Account Sub Head */}
                <Col md={3}>
                  <Label className="form-label">{t("Account Sub-Head")}</Label>
                  <Select
                    value={selectedSubHead}
                    onChange={(option) => setSelectedSubHead(option)}
                    options={subHeads}
                    styles={selectStyles}
                    isClearable
                    placeholder={t("All Sub-Heads")}
                    disabled={!selectedHead}
                  />
                </Col>

                {/* Transactor Type */}
                <Col md={3}>
                  <Label className="form-label">{t("Transactor Type")}</Label>
                  <Select
                    value={transactorType}
                    onChange={(option) => setTransactorType(option)}
                    options={transactorTypeOptions}
                    styles={selectStyles}
                    isClearable
                    placeholder={t("Choose Transactor Type")}
                  />
                </Col>

                {/* Transactor */}
                <Col md={3}>
                  <Label className="form-label">{t("Transactor")}</Label>
                  <Select
                    value={selectedTransactor}
                    onChange={(option) => setSelectedTransactor(option)}
                    options={transactors}
                    styles={selectStyles}
                    isClearable
                    placeholder={t("Select Transactor")}
                    disabled={!transactorType}
                    isLoading={loadingTransactors}
                  />
                </Col>

                {/* Amount Filter */}
                <Col md={3}>
                  <Label className="form-label">{t("Amount Filter")}</Label>
                  <Select
                    value={amountFilter}
                    onChange={(option) => setAmountFilter(option)}
                    options={amountFilterOptions}
                    styles={selectStyles}
                    isClearable
                    placeholder={t("Operator")}
                  />
                </Col>

                {/* Amount Val */}
                <Col md={3}>
                  <Label className="form-label">{t("Amount")}</Label>
                  <Input
                    type="number"
                    step="0.01"
                    className="form-control"
                    placeholder={t("Enter amount")}
                    value={amountVal}
                    onChange={(e) => setAmountVal(e.target.value)}
                  />
                </Col>

                {/* Action Buttons */}
                <Col md={3} className="d-flex align-items-end justify-content-end gap-2">
                  <Button color="secondary" className="w-50 d-flex align-items-center justify-content-center gap-1 btn-md" onClick={handleReset}>
                    <RefreshCw size={16} />
                    {t("Reset")}
                  </Button>
                  <Button color="primary" className="w-50 d-flex align-items-center justify-content-center gap-1 btn-md" onClick={handleSearch} disabled={loading}>
                    {loading ? <ClipLoader size={16} color="#fff" /> : <Search size={16} />}
                    {t("Search")}
                  </Button>
                </Col>
              </Row>
            </CardBody>
          </Card>

          {/* Summary Cards */}
          {reportData.length > 0 && (
            <Row className="mb-4">
              <Col md={4}>
                <Card className="mini-stats-wid shadow-sm border-0 h-100">
                  <CardBody className="d-flex flex-column justify-content-center p-4">
                    <p className="text-muted fw-medium mb-1">{t("Total Debits")}</p>
                    <h4 className="mb-0 text-success fw-bold">
                      {totalDebits.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </h4>
                  </CardBody>
                </Card>
              </Col>
              <Col md={4}>
                <Card className="mini-stats-wid shadow-sm border-0 h-100">
                  <CardBody className="d-flex flex-column justify-content-center p-4">
                    <p className="text-muted fw-medium mb-1">{t("Total Credits")}</p>
                    <h4 className="mb-0 text-danger fw-bold">
                      {totalCredits.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </h4>
                  </CardBody>
                </Card>
              </Col>
              <Col md={4}>
                <Card className="mini-stats-wid shadow-sm border-0 h-100">
                  <CardBody className="d-flex flex-column justify-content-center p-4">
                    <p className="text-muted fw-medium mb-1">{t("Net Balance")}</p>
                    <h4 className={`mb-0 fw-bold ${netBalance >= 0 ? "text-success" : "text-danger"}`}>
                      {netBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </h4>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}

          {/* Results Card */}
          <Card className="shadow-sm border-0">
            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
                <h5 className="card-title mb-0" style={{ fontWeight: 600, color: "#495057" }}>
                  {t("Ledger Entries")} ({reportData.length})
                </h5>
                {reportData.length > 0 && (
                  <div className="d-flex gap-2">
                    <Button color="success" outline className="d-flex align-items-center gap-1 btn-sm px-3" onClick={handleExportToExcel}>
                      <FileSpreadsheet size={16} />
                      {t("Excel")}
                    </Button>
                    {/* <Button color="primary" outline className="d-flex align-items-center gap-1 btn-sm px-3" onClick={handlePrint}>
                      <Printer size={16} />
                      {t("Print")}
                    </Button> */}
                  </div>
                )}
              </div>

              {loading ? (
                <div className="d-flex justify-content-center align-items-center py-5">
                  <ClipLoader size={40} color="#556ee6" />
                </div>
              ) : reportData.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <p className="mb-0 fs-5">{t("No ledger entries found for the selected criteria.")}</p>
                </div>
              ) : (
                <div className="table-responsive" ref={printRef}>
                  <Table className="table align-middle table-nowrap mb-0 table-hover">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: "60px" }}>{t("S.No")}</th>
                        <th>{t("Date")}</th>
                        <th>{t("Branch")}</th>
                        <th>{t("Ref No")}</th>
                        <th>{t("Type")}</th>
                        <th>{t("Account Hierarchy")}</th>
                        <th>{t("Transactor")}</th>
                        <th className="text-end">{t("Debit (Dr)")}</th>
                        <th className="text-end">{t("Credit (Cr)")}</th>
                        <th>{t("Notes")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedData.map((item, index) => {
                        const serialNumber = pageSize === -1 ? (index + 1) : ((currentPage - 1) * pageSize + index + 1);
                        return (
                          <tr key={item.ledgerPostingId}>
                            <td>{serialNumber}</td>
                            <td>{formatDateForDisplay(item.transactionDate)}</td>
                            <td>
                              <span className="fw-medium text-dark">{item.branchName || "N/A"}</span>
                            </td>
                            <td>
                              <code className="text-primary fw-medium">{item.referenceNo || "N/A"}</code>
                            </td>
                            <td>
                              <span className={`badge badge-pill badge-soft-${getTransactionTypeBadgeClass(item.transactionType)} font-size-11 p-2`}>
                                {item.transactionType}
                              </span>
                            </td>
                            <td>
                              <div className="text-wrap" style={{ maxWidth: "250px" }}>
                                <small className="text-muted d-block">{item.accountMasterName}</small>
                                <small className="text-dark d-block fw-medium">{item.accountHeadName}</small>
                                <span className="text-primary fw-semibold">{item.accountSubHeadName}</span>
                              </div>
                            </td>
                            <td>
                              {item.transactorType ? (
                                <div>
                                  <small className="text-muted d-block">{item.transactorType}</small>
                                  <span className="fw-medium text-dark">{item.transactorName}</span>
                                </div>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                            <td className="text-end fw-semibold text-success">
                              {item.debitAmt > 0
                                ? item.debitAmt.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                : "-"}
                            </td>
                            <td className="text-end fw-semibold text-danger">
                              {item.creditAmt > 0
                                ? item.creditAmt.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                : "-"}
                            </td>
                            <td>
                              <div className="text-wrap text-muted" style={{ maxWidth: "200px" }}>
                                {item.notes || "-"}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              )}

              {/* Pagination Controls */}
              {!loading && reportData.length > 25 && (
                <div className="d-flex justify-content-between align-items-center mt-4 p-3 border-top" style={{ backgroundColor: '#fcfcfd' }}>
                  <div className="text-muted font-size-13">
                    Showing <span className="fw-semibold text-primary">
                      {pageSize === -1 ? 1 : (currentPage - 1) * pageSize + 1}
                    </span> to{" "}
                    <span className="fw-semibold text-primary">
                      {pageSize === -1 ? totalCount : Math.min(currentPage * pageSize, totalCount)}
                    </span> of{" "}
                    <span className="fw-semibold text-primary">{totalCount}</span> entries
                  </div>
                  
                  <div className="d-flex align-items-center gap-3">
                    {/* Page Size Select */}
                    <div className="d-flex align-items-center gap-2">
                      <span className="text-muted font-size-13">Show:</span>
                      <select
                        className="form-select form-select-sm"
                        style={{ width: '80px', borderRadius: '4px' }}
                        value={pageSize}
                        onChange={(e) => {
                          const val = parseInt(e.target.value, 10);
                          setPageSize(val);
                          setCurrentPage(1);
                        }}
                      >
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                        <option value={-1}>All</option>
                      </select>
                    </div>

                    {/* Pagination Buttons */}
                    {pageSize !== -1 && totalPages > 1 && (
                      <div className="d-flex gap-1">
                        <Button
                          color="light"
                          size="sm"
                          disabled={currentPage === 1}
                          onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                          style={{ width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        >
                          <ChevronLeft size={16} />
                        </Button>

                        {[...Array(totalPages)].map((_, idx) => {
                          const pageNum = idx + 1;
                          // Show first, last, current, and page numbers around current
                          if (
                            pageNum === 1 ||
                            pageNum === totalPages ||
                            (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                          ) {
                            const isActive = currentPage === pageNum;
                            return (
                              <Button
                                key={pageNum}
                                color={isActive ? "primary" : "light"}
                                size="sm"
                                onClick={() => setCurrentPage(pageNum)}
                                style={{
                                  minWidth: '32px',
                                  height: '32px',
                                  fontWeight: isActive ? '600' : '400',
                                  backgroundColor: isActive ? '#556ee6' : '#fff',
                                  borderColor: isActive ? '#556ee6' : '#dee2e6',
                                  color: isActive ? '#fff' : '#495057'
                                }}
                              >
                                {pageNum}
                              </Button>
                            );
                          } else if (
                            (pageNum === currentPage - 2 && currentPage > 3) ||
                            (pageNum === currentPage + 2 && currentPage < totalPages - 2)
                          ) {
                            return (
                              <span key={pageNum} className="px-1 align-self-center text-muted" style={{ userSelect: 'none' }}>
                                ...
                              </span>
                            );
                          }
                          return null;
                        })}

                        <Button
                          color="light"
                          size="sm"
                          disabled={currentPage === totalPages}
                          onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                          style={{ width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        >
                          <ChevronRight size={16} />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
}
